import { Button, CircularProgress, IconButton, Paper, Typography } from '@mui/material';
import {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import L from 'leaflet';
import { TileLayer, MapContainer, Tooltip, CircleMarker, useMapEvents, Polyline, LayersControl } from 'react-leaflet';

import { useDispatch, useSelector } from 'react-redux';
import { Input, Satellite } from '@mui/icons-material';
import { useTheme } from '@mui/styles';
import { selectUser } from '../../reducers/userSlice';

import { useSocket } from '../../context/useSocket';
import { setPadData } from '../../reducers/rtfoSlice';
import { useSnackbar } from 'notistack';
import { RTFOCreateNewPad } from './RTFOCreateNewPad';
import { getRTFOClientName, getRTFOCompanyName } from './rtfoConfig';
import ScrollZoomControl from './ScrollZoomControl';
import ScrollZoomControlOverlay from './ScrollZoomControlOverlay';
import { RTFOPadsListContainer } from './RTFOPadsListContainer';
import { RTFOPadsListFilter } from './RTFOPadsListFilter';
import { RTFOMapMeasureControl } from './RTFOMapMeasureControl';
import { colors } from '../../helpers/colors';

const { BaseLayer, Overlay } = LayersControl;

const ENV = process.env.REACT_APP_NODE_ENV;
const BASE_URL_SSD = ENV === 'production' ? process.env.REACT_APP_BASE_URL_SSD : (ENV === 'staging' || ENV === 'development') ? process.env.REACT_APP_BASE_URL_SSD_STAGING : 'http://localhost:8080/api';


const RTFOMapPadActivity = () => {

  const theme = useTheme();

  const { socket } = useSocket();
  const { enqueueSnackbar } = useSnackbar();

  const mapRef = useRef(null);
  const user = useSelector(selectUser);

  const darkMode = user?.value?.darkMode;
  const dispatch = useDispatch();

  const [padActivity, setPadActivity] = useState(null);
  const [formations, setFormations] = useState([]);
  const [currentPadName, setCurrentPadName] = useState("");
  const [checked, setChecked] = useState([]);
  const [zoomLevel, setZoomLevel] = useState(9);
  const [createNewPadOpen, setCreateNewPadOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [newPad, setNewPad] = useState(null);
  const [scrollZoomControlOverlayVisible, setScrollZoomControlOverlayVisible] = useState(false);
  const [filters, setFilters] = useState({});
  const [padsListFilterVisible, setPadsListFilterVisible] = useState(false);
  const [tilesType, setTilesType] = useState('map');

  const isLoading = useRef(true);

  const filteredPadActivity = useMemo(() => {
    try {
      if (!padActivity?.length > 0) return [];

      if (!filters || Object.keys(filters)?.length === 0) return padActivity;

      let filteredPads = [...padActivity];

      if (filters.dateFrom) {
        filteredPads = filteredPads.filter(pad => new Date(pad.createdOn)?.getTime() >= new Date(filters.dateFrom)?.getTime()) || [];
      };
      if (filters.dateTo) {
        filteredPads = filteredPads.filter(pad => new Date(pad.createdOn)?.getTime() <= new Date(filters.dateTo)?.getTime()) || [];
      };
      if (filters.status) {
        if (filters.status?.length === 1) {
          // if (!filters.status.every(s => ['Active', 'Completed'].includes(s))) {
          filteredPads = filteredPads.filter(pad => filters.status?.[0] === 'Active' ? pad.isActive : !pad.isActive) || [];
        };
      };
      if (filters.formation) {
        filteredPads = filteredPads.filter(pad => filters.formation.some(val => pad.formation === val)) || [];
      };
      if (filters.area) {
        filteredPads = filteredPads.filter(pad => filters.area.some(val => pad.area === val)) || [];
      };
      if (filters.client) {
        filteredPads = filteredPads.filter(pad => filters.client.some(val => getRTFOCompanyName(pad.client) === val)) || [];
      };

      setCurrentPadName("");

      return filteredPads;

    } catch (error) {
      console.log(error);
      return [];
    };
  }, [filters, padActivity]);

  if (socket) {
    socket.onmessage = (event) => {
      const response = JSON.parse(event?.data);

      if (!response) return;

      if (response.messageType === 'padData') {
        const padData = response?.payload;

        if (padData) {

          if (padData.stages?.length > 0) {
            dispatch(setPadData({ ...padData, isLoadingData: true }));

            enqueueSnackbar(`Downloading pad ${padData?.name} with ${padData?.stages?.length || 0} stages...`, {
              variant: 'info',
              persist: true
            });
          } else {
            dispatch(setPadData(padData));
          };


          // request seismicity data
          socket?.send(JSON.stringify({
            messageType: 'getSeismicityData',
            padName: padData?.name
          }));

        };
      } else if (response.messageType === 'padAccessError') {
        enqueueSnackbar(`Your account does not have access to pad ${response?.padName}`, {
          variant: 'error',
          persist: false
        });
      };
    };
  };

  // Fetch initial pad activity
  useEffect(() => {
    const fetchData = async () => {

      try {
        const result = await fetch(`${BASE_URL_SSD}/rtfo/activity`, {
          method: 'GET',
          headers: {
            'x-api-key': process.env.REACT_APP_API_KEY,
            client: getRTFOClientName(user?.value?.email)
          }
        });

        const activity = await result.json();

        activity.sort((a, b) => new Date(b?.createdOn) - new Date(a?.createdOn))?.filter(a => a.name && a.formation && a.area);

        setPadActivity(activity);
        const newFormations = [...new Set(activity?.map(p => `${p.formation} - ${p.area}`))];

        setFormations(newFormations);
        // setOpen(newFormations);
        const allPads = activity?.map(p => p.name);
        setChecked(allPads);

        isLoading.current = false;
      } catch (error) {
        console.log(error);

        isLoading.current = false;
      };

    };

    if (!padActivity) {
      fetchData();
    };

  }, [padActivity, user?.value?.email]);


  // Calculate pad coordinates
  const padCoordinates = useMemo(() => {
    const coords = {};
    if (!filteredPadActivity || filteredPadActivity?.length === 0) return [];

    for (let pad of filteredPadActivity) {
      if (!pad) continue;

      const wellCoords = {};

      const wells = Object.keys(pad?.surveys?.wells || {});

      for (let i = 0; i < wells.length; i++) { // calculate well paths
        const lat = pad?.surveys?.wells?.[wells[i]]?.surfaceLat;
        const lon = pad?.surveys?.wells?.[wells[i]]?.surfaceLon;
        const surveyData = pad?.surveys?.wells?.[wells[i]]?.surveyData;
        const wellLatLngs = surveyToCoordinates(lat, lon, surveyData);

        if (!wellCoords[wells[i]]) wellCoords[wells[i]] = [];

        wellCoords[wells[i]] = wellLatLngs;
      };

      const wellCoordinatesArray = Object.values(wellCoords);
      const center = getMiddlePoint(wellCoordinatesArray);
      coords[`${pad.name}`] = center;
    };

    return coords;
  }, [filteredPadActivity]);

  // Calculate well coordinates
  const wellCoordinates = useMemo(() => {
    const coords = {};
    if (!filteredPadActivity || filteredPadActivity.length === 0) return [];

    let sortedPadActivity = [...filteredPadActivity] || [];

    // move recent pads to the top
    sortedPadActivity = sortedPadActivity.sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn)) || [];

    // move the selected pad to the top
    if (showDetails) {
      sortedPadActivity = sortedPadActivity.filter(a => a.name !== showDetails);
      sortedPadActivity.push(filteredPadActivity.find(a => a.name === showDetails));
    };

    for (let pad of sortedPadActivity) {
      if (!pad) continue;

      const wellCoords = {};

      const wells = Object.keys(pad?.surveys?.wells || {});

      for (let i = 0; i < wells.length; i++) { // calculate well paths
        const lat = pad?.surveys?.wells?.[wells[i]]?.surfaceLat;
        const lon = pad?.surveys?.wells?.[wells[i]]?.surfaceLon;
        const surveyData = pad?.surveys?.wells?.[wells[i]]?.surveyData;
        const wellLatLngs = surveyToCoordinates(lat, lon, surveyData);

        if (!wellCoords[wells[i]]) wellCoords[wells[i]] = [];

        wellCoords[wells[i]] = wellLatLngs;
      };

      coords[`${pad.name}`] = wellCoords;
    };

    return coords;
  }, [filteredPadActivity, showDetails]);

  const globalCenter = useMemo(() => {

    if (!filteredPadActivity) return [];

    const allPads = Object.keys(wellCoordinates);

    const lines = [];

    for (let pad of allPads) {
      const wells = Object.keys(wellCoordinates[pad]);

      for (let well of wells) {
        const wellCoords = wellCoordinates[pad][well];
        if (wellCoords.length > 0) {
          lines.push(wellCoords);
        };
      };
    };

    let center = getMiddlePoint(lines);
    if (center?.[0] === 0 || center?.[1] === 0) {
      center = [55.000000, -115.000000];
    };

    return center;
  }, [filteredPadActivity, wellCoordinates]);

  useEffect(() => { // pan to pad when pad name changes
    if (currentPadName?.length > 0 && mapRef?.current && padCoordinates[currentPadName]) {
      mapRef?.current?.flyTo(padCoordinates[currentPadName], 14, { animate: true, duration: 1 });
    };
  }, [currentPadName, padCoordinates]);

  useEffect(() => { // pan to center when filters change
    mapRef?.current?.flyTo(globalCenter, 8, { animate: true, duration: 1 });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  if (globalCenter?.length > 0 && !isLoading.current) {
    return (
      <Paper style={{ display: 'flex', flexDirection: 'row', height: 'calc(100%-20px)', width: '100%', marginTop: 20, backgroundColor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')], position: 'relative' }} elevation={0} sx={{ boxShadow: 1 }}>

        <RTFOPadsListContainer
          padActivity={filteredPadActivity}
          formations={formations}
          darkMode={darkMode}
          setCreateNewPadOpen={setCreateNewPadOpen}
          handleSelectPad={handleSelectPad}
          currentPadName={currentPadName}
          handleTogglePadVisibility={handleCheck}
          visiblePadNames={checked}
          filters={filters}
          handlePadsListFilterVisible={handlePadsListFilterVisible}
        />

        {padsListFilterVisible && <RTFOPadsListFilter padActivity={padActivity} filters={filters} setFilters={setFilters} setPadsListFilterVisible={setPadsListFilterVisible} setCurrentPadName={setCurrentPadName} darkMode={darkMode} />}

        <IconButton
          color={tilesType === 'map' || darkMode ? '#333' : 'primary'}
          elevation={4}
          sx={{
            backgroundColor: tilesType === 'map' ? (darkMode ? '#253238' : "#f2f2f2") : "#00000000",
            position: 'absolute',
            left: 330,
            top: 128,
            zIndex: 999,
            height: 34,
            width: 34,
            boxSizing: "border-box",
            border: darkMode ? '2px solid #39393a' : " 2px solid #bfbfbf",
            borderRadius: '4px',
          }}
          title="Toggle Satellite View"
          onClick={() =>
            setTilesType((prev) => (prev === 'map' ? 'satellite' : 'map'))
          }
          size="small"
        >
          <Satellite
            style={{
              height: 22,
              width: 22,
              color: tilesType === 'map'
                ? (darkMode === true ? "#f2f2f2" : "black")
                : colors[0]
            }}
          />
        </IconButton>

        <MapContainer
          center={globalCenter}
          zoom={zoomLevel}
          maxZoom={16}
          ref={mapRef}
          style={{
            height: '100%',
            width: '100%',
            backgroundColor: darkMode ? 'black' : 'white'
          }}
          attributionControl={true}
          zoomControl={true}
          scrollWheelZoom={false}
        >

          <LayersControl position="topright">
            <BaseLayer checked name="Base Map">
              {tilesType === 'satellite' ?
                <TileLayer
                  url={`https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}`}
                  removeAttribution
                  attributionControl="false"
                />
                : darkMode ?
                  <TileLayer url={`https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}`} /> :
                  <TileLayer url={`https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}`} />}
            </BaseLayer>
            <Overlay checked name="Boundaries">
              <TileLayer url={`https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places_Alternate/MapServer/tile/{z}/{y}/{x}`} />
            </Overlay>
          </LayersControl>

          {Object.keys(wellCoordinates)?.filter(p => checked.indexOf(p) !== -1)?.sort((a, b) => b.isActive - a.isActive)?.map(pad => {

            const padData = padActivity?.find(p => p.name === pad);
            const padMiddlePoint = getPadMiddlePoint(wellCoordinates, padData?.surveys, pad);

            return (
              Object.keys(wellCoordinates?.[pad])?.map((well, j) => {
                return (
                  <div key={`${pad}-${well}`}>
                    <Polyline pathOptions={{ color: currentPadName === pad ? 'tomato' : padData.isActive ? 'green' : 'black', weight: 4 }} positions={wellCoordinates?.[pad]?.[well]} bounds={L.latLngBounds(wellCoordinates?.[pad]?.[well])} />
                    <CircleMarker
                      center={[padActivity?.find(p => p.name === pad)?.surveys?.wells?.[well]?.surfaceLat, padActivity?.find(p => p.name === pad)?.surveys?.wells?.[well]?.surfaceLon]}
                      pathOptions={{ color: currentPadName === pad ? 'tomato' : padData.isActive ? 'green' : 'black' }}
                      radius={5}
                    />
                    <CircleMarker
                      center={padMiddlePoint}
                      pathOptions={{ color: 'transparent' }}
                      radius={0}
                    >
                      {zoomLevel > 8 && j === 0 && <Tooltip className={darkMode ? "map-tooltip-dark" : "map-tooltip"} direction={"bottom"} offset={[0, 0]} opacity={1} permanent interactive={true}>
                        <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', backgroundColor: theme?.palette?.paper?.[darkMode ? 'dark' : 'light'] }} onClick={() => {
                          handleSelectPad(pad);
                          if (currentPadName === pad && showDetails === pad) {
                            setShowDetails(false);
                          };
                          // setShowDetails(prev => prev === pad ? false : pad);
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 0, padding: '0 4px', backgroundColor: theme?.palette?.paper?.[darkMode ? 'dark' : 'light'] }} >
                            <Typography fontSize="12px" textAlign={'center'} color={darkMode ? 'white' : 'black'}>{pad}</Typography>
                          </div>
                          {showDetails === pad &&
                            <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', padding: 4, backgroundColor: theme?.palette?.paper?.[darkMode ? 'dark' : 'light'] }}>
                              <Typography variant='subtitle2' color={darkMode ? 'white' : 'black'}>Wells: {Object.keys(padData?.surveys?.wells)?.length || 0}</Typography>
                              <Typography variant='subtitle2' color={darkMode ? 'white' : 'black'}>Stages: {padData?.stages?.length || 0}</Typography>
                              <Typography variant='subtitle2' color={darkMode ? 'white' : 'black'}>Status: {padData?.isActive ? 'Active' : 'Completed'}</Typography>
                              <Typography variant='subtitle2' color={darkMode ? 'white' : 'black'}>Created: {new Date(padData?.createdOn)?.toLocaleString()}</Typography>
                              <Typography variant='subtitle2' color={darkMode ? 'white' : 'black'}>Modified: {new Date(padData?.lastModified)?.toLocaleString()}</Typography>
                              <Typography variant='subtitle2' textAlign={'center'} color={darkMode ? 'white' : 'black'}>UWIs</Typography>
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {(!padData?.wellMapping || padData?.wellMapping?.length === 0) ? [] : Object.values(padData?.wellMapping)?.map((w, i) => (
                                  <Typography textAlign={'center'} fontSize={13} color={darkMode ? 'white' : 'black'} key={w + '-' + i}>{w}</Typography>
                                ))}
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: 5 }}>
                                <Button onClick={() => handleLoadPadDataClick(pad)} size="small" style={{ height: 28, color: (darkMode ? '#fff' : theme.palette.primary.main) }} startIcon={<Input fontSize='16px' />}><Typography variant='subtitle2'>Load Pad Data</Typography></Button>
                              </div>
                            </div>
                          }
                        </div>
                      </Tooltip>}
                    </CircleMarker>
                  </div>
                )
              })
            )

          })}

          <RTFOMapMeasureControl />
          <ZoomHelper />
          <ScrollZoomControl setScrollZoomControlOverlayVisible={setScrollZoomControlOverlayVisible} darkMode={darkMode} />

          {scrollZoomControlOverlayVisible && <ScrollZoomControlOverlay setScrollZoomControlOverlayVisible={setScrollZoomControlOverlayVisible} darkMode={darkMode} />}

        </MapContainer>

        {createNewPadOpen && <RTFOCreateNewPad
          newPad={newPad}
          setNewPad={setNewPad}
          setCreateNewPadOpen={setCreateNewPadOpen}
          padActivity={padActivity}
          setPadActivity={setPadActivity}
          setFormations={setFormations}
          setChecked={setChecked}
          darkMode={darkMode}
          userEmail={user?.value?.email}
        />}

      </Paper>
    );
  }
  else {
    return (
      <div style={{ width: '100vw', height: '100vh', position: 'relative' }}>
        <LoadingRTFOData darkMode={darkMode} />
      </div>
    );
  };


  function handleLoadPadDataClick(padName) {
    socket?.readyState === 1 &&
      socket?.send(JSON.stringify({
        messageType: 'getPadData',
        padName,
        initialLoad: true,
        getFullData: user?.value?.email === 'bhepburn@wcap.ca' || user?.value?.email === 'wcap@ssdynamics.com' || user?.value?.email === 'bheppie@shaw.ca',
        userEmail: user?.value?.email,
        client: getRTFOClientName(user?.value?.email)
      }));
  };

  function handleSelectPad(padName) {
    if (currentPadName !== padName) {
      setCurrentPadName(padName);
    };
    if (showDetails !== padName) {
      setShowDetails(padName);
    };
  };

  function handlePadsListFilterVisible() {
    setPadsListFilterVisible(prev => !prev);
  };

  function ZoomHelper() { // This component is used to get the zoom level of the map

    const mapEvents = useMapEvents({
      zoomend: () => {
        setZoomLevel(mapEvents.getZoom());
      },
    });

    return null;
  };

  function handleCheck(e, padName) {
    e.stopPropagation();
    if (checked?.includes(padName)) {
      setChecked(checked?.filter(p => p !== padName));
    } else {
      setChecked([...checked, padName]);
    };
  };

};

export default RTFOMapPadActivity;

function LoadingRTFOData({ darkMode }) {
  return (
    <div style={{ position: 'absolute', zIndex: 666, width: '100%', height: '100%', backgroundColor: darkMode ? 'black' : 'rgba(255,255,255,0.8)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
      <CircularProgress />
      <Typography style={{ marginTop: 20 }}>Loading pad activity...</Typography>
    </div>
  );
};

function surveyToCoordinates(surfaceLat, surfaceLon, surveyData) {
  try {
    let latLngs = [];

    // Initialize variables for minimum curvature algorithm
    let x0 = surfaceLat;
    let y0 = surfaceLon;
    let z0 = 0;
    let mdPrev = 0;

    // Iterate through survey points to calculate wellbore path
    for (let i = 0; i < surveyData.length; i++) {
      let md = surveyData[i].measuredDepth;
      let dip = surveyData[i].deviation;
      let azi = surveyData[i].direction;
      let dMD = md - mdPrev;
      let radians = dip * Math.PI / 180;
      let aziRad = azi * Math.PI / 180;

      // Calculate new coordinates with minimum curvature algorithm
      let dx = dMD * Math.sin(radians) * Math.cos(aziRad);
      let dy = dMD * Math.sin(radians) * Math.sin(aziRad);
      let dz = dMD * Math.cos(radians);
      let x = x0 + dx / 111111;
      let y = y0 + dy / (111111 * Math.cos(surfaceLat * Math.PI / 180));
      let z = z0 + dz;
      let lat = x;
      let lon = y;

      // Add the lat, lon coordinates to the array
      latLngs.push([lat, lon]);

      // Update variables for next iteration
      x0 = x;
      y0 = y;
      z0 = z;
      mdPrev = md;

    };

    return latLngs;

  } catch (error) {
    console.log(error);
    return [];
  };
};

function getMiddlePoint(lines) {
  try {
    let totalLat = 0;
    let totalLon = 0;
    let totalPoints = 0;

    for (const line of lines) {
      let lineLat = 0;
      let lineLon = 0;
      const numPoints = line?.length;

      for (const point of line) {
        lineLat += point[0];
        lineLon += point[1];
      };

      totalLat += lineLat / numPoints;
      totalLon += lineLon / numPoints;
      totalPoints++;
    };

    const avgLat = totalLat / totalPoints;
    const avgLon = totalLon / totalPoints;

    if (isNaN(avgLat) || isNaN(avgLon)) {
      return [0, 0];
    };

    return [avgLat, avgLon];

  } catch (error) {
    console.log(error);
    return [0, 0];
  };
};

function getPadMiddlePoint(wellCoordinates, surveys, padName) {
  try {

    const padWellCoordinates = wellCoordinates[padName];
    const padWellNames = Object.keys(padWellCoordinates);
    const horizCoordinates = [];

    for (const wellName of padWellNames) {
      const horizStartIndex = Math.max(surveys?.wells?.[wellName]?.surveyData?.findIndex(d => d.deviation > 80), 0);
      horizCoordinates.push(padWellCoordinates[wellName].slice(horizStartIndex,));
    };

    const padMiddlePoint = getMiddlePoint(horizCoordinates);
    return padMiddlePoint;

  } catch (error) {
    console.log(error)
    return [0, 0];
  };
};
